import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { builderSurfaceJsonContentHeader } from '../../../../common/utils/restApiHeaderUtils';
import { fetchProjectUsage } from '../../../../features/vibe-coding/services/vibeCodingService';
import EmbeddedPageFrame from '../../../common/ui/EmbeddedPageFrame';

const getWalletUrl = (version, endpoint) =>
  `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/vibe-coding/wallet/${endpoint}`;

const getErrorMessage = (error) =>
  (error.response &&
    error.response.data &&
    (error.response.data.message || error.response.data.error)) ||
  error.message ||
  'Unable to load API usage.';

const formatNumber = (value, maximumFractionDigits = 2) =>
  value !== null && value !== '' && Number.isFinite(Number(value))
    ? Number(value).toLocaleString(undefined, { maximumFractionDigits })
    : '--';

const formatUsd = (value) =>
  value !== null && value !== '' && Number.isFinite(Number(value))
    ? Number(value).toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 4,
        maximumFractionDigits: 6,
      })
    : '--';

const formatTimestamp = (value) => {
  if (!value) return '--';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const ApiUsage = ({ embedded, match }) => {
  const { projectId, version } = match.params;
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState('');
  const [usages, setUsages] = useState([]);
  const [totals, setTotals] = useState(null);
  const [totalRequests, setTotalRequests] = useState(0);
  const [totalCalls, setTotalCalls] = useState(0);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const balanceRequestId = useRef(0);
  const usageRequestId = useRef(0);

  useEffect(() => {
    const requestId = ++balanceRequestId.current;
    if (!projectId || !version) {
      setBalanceError('Project and version are required to load the wallet balance.');
      setBalanceLoading(false);
      return () => {
        balanceRequestId.current += 1;
      };
    }

    setBalanceLoading(true);
    setBalanceError('');
    axios
      .get(
        getWalletUrl(version, 'balance'),
        builderSurfaceJsonContentHeader(projectId, true),
      )
      .then(({ data }) => {
        if (balanceRequestId.current !== requestId) return;
        if (!data || data.success === false) {
          throw new Error((data && data.message) || 'Unable to load wallet balance.');
        }
        setBalance(data);
      })
      .catch((error) => {
        if (balanceRequestId.current === requestId) {
          setBalanceError(getErrorMessage(error));
        }
      })
      .finally(() => {
        if (balanceRequestId.current === requestId) setBalanceLoading(false);
      });

    return () => {
      balanceRequestId.current += 1;
    };
  }, [projectId, version, refreshKey]);

  useEffect(() => {
    const requestId = ++usageRequestId.current;
    if (!projectId || !version) {
      setUsages([]);
      setTotals(null);
      setTotalRequests(0);
      setTotalCalls(0);
      setUsageError('Project and version are required to load model usage.');
      setUsageLoading(false);
      return () => {
        usageRequestId.current += 1;
      };
    }

    setUsageLoading(true);
    setUsageError('');
    fetchProjectUsage({ version, projectId })
      .then((response) => {
        if (usageRequestId.current !== requestId) return;
        if (!response || response.success === false || !Array.isArray(response.usages)) {
          throw new Error((response && response.message) || 'Unable to load project usage.');
        }
        setUsages(response.usages);
        setTotals(response.totals || {});
        setTotalRequests(Number(response.totalRequests) || response.usages.length);
        setTotalCalls(Number(response.totals && response.totals.calls) || 0);
      })
      .catch((error) => {
        if (usageRequestId.current === requestId) {
          setUsageError(getErrorMessage(error));
        }
      })
      .finally(() => {
        if (usageRequestId.current === requestId) setUsageLoading(false);
      });

    return () => {
      usageRequestId.current += 1;
    };
  }, [projectId, version, refreshKey]);

  const refresh = () => setRefreshKey((current) => current + 1);
  return (
    <EmbeddedPageFrame embedded={embedded}>
      <div className="api-usage">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h3 className="mb-1">API Usage</h3>
            <p className="text-muted mb-0">Project-wide model usage by request.</p>
          </div>
          <button type="button" className="btn btn-default" onClick={refresh}>
            <i className="wb webconnect-refresh" aria-hidden="true" /> Refresh
          </button>
        </div>

        <section className="card mb-4" aria-labelledby="api-usage-balance-title">
          <div className="card-content p-3">
            <h4 id="api-usage-balance-title" className="mt-0">
              Current wallet balance
            </h4>
            {balanceError ? <div className="alert alert-danger">{balanceError}</div> : null}
            <div className="mb-2">
              <div className="text-muted">Available balance</div>
              <strong className="h3">
                {balanceLoading && !balance
                  ? 'Loading…'
                  : formatUsd(balance && balance.balanceUSD)}
              </strong>
            </div>
            <small className="text-muted">
              This is the latest available balance, not a usage-history total.
            </small>
          </div>
        </section>

        <section aria-labelledby="api-usage-requests-title">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div>
              <h4 id="api-usage-requests-title" className="mb-1">
                Model usage requests
              </h4>
              <p className="text-muted mb-0">
                {formatNumber(totalRequests, 0)} total requests across{' '}
                {formatNumber(totalCalls, 0)} model calls.
              </p>
            </div>
          </div>

          {usageError ? <div className="alert alert-danger">{usageError}</div> : null}

          <div className="table-responsive">
            <table className="table table-hover table-striped">
              <thead>
                <tr>
                  <th>Request / run ID</th>
                  <th>Conversation ID</th>
                  <th>Input tokens</th>
                  <th>Output tokens</th>
                  <th>Total tokens</th>
                  <th>Cached tokens</th>
                  <th>Cache write tokens</th>
                  <th>Coins used</th>
                  <th>Created</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {usageLoading ? (
                  <tr>
                    <td colSpan="10" className="text-center text-muted">
                      Loading model usage…
                    </td>
                  </tr>
                ) : null}
                {!usageLoading && usageError ? (
                  <tr>
                    <td colSpan="10" className="text-center text-muted">
                      Model usage could not be loaded.
                    </td>
                  </tr>
                ) : null}
                {!usageLoading && !usageError && usages.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center text-muted">
                      No model usage requests recorded for this project.
                    </td>
                  </tr>
                ) : null}
                {!usageLoading && !usageError
                  ? usages.map((usage, index) => (
                      <tr
                        key={
                          usage.requestId ||
                          usage.runId ||
                          usage.taskId ||
                          `${usage.conversationId || 'request'}-${index}`
                        }
                      >
                        <td className="text-monospace">
                          {usage.requestId || usage.runId || usage.taskId || '--'}
                        </td>
                        <td className="text-monospace">{usage.conversationId || '--'}</td>
                        <td>{formatNumber(usage.inputTokens, 0)}</td>
                        <td>{formatNumber(usage.outputTokens, 0)}</td>
                        <td>{formatNumber(usage.totalTokens, 0)}</td>
                        <td>{formatNumber(usage.cachedTokens, 0)}</td>
                        <td>{formatNumber(usage.cacheWriteTokens, 0)}</td>
                        <td>{formatNumber(usage.coinsUsed)}</td>
                        <td>{formatTimestamp(usage.createdAt)}</td>
                        <td>{formatTimestamp(usage.updatedAt)}</td>
                      </tr>
                    ))
                  : null}
                {!usageLoading && !usageError && totals ? (
                  <tr className="font-weight-bold">
                    <td>Project total</td>
                    <td>--</td>
                    <td>{formatNumber(totals.inputTokens, 0)}</td>
                    <td>{formatNumber(totals.outputTokens, 0)}</td>
                    <td>{formatNumber(totals.totalTokens, 0)}</td>
                    <td>{formatNumber(totals.cachedTokens, 0)}</td>
                    <td>{formatNumber(totals.cacheWriteTokens, 0)}</td>
                    <td>{formatNumber(totals.coinsUsed)}</td>
                    <td>--</td>
                    <td>--</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </EmbeddedPageFrame>
  );
};

ApiUsage.propTypes = {
  embedded: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      projectId: PropTypes.string,
      version: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default ApiUsage;
